import { Grid } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { ActionButton } from '@tassat/button';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { UserRecord } from '@tassat/fred-definitions';
import { AdminUserRecordMapped } from '@tassat/fred-extended-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import Worker from 'Lib/worker/PrimusWorker';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';

interface UserAuthActionsProps extends WithStyles<typeof muiStyles>{
    user: AdminUserRecordMapped | UserRecord;
    firmId?: string;
}

const muiStyles = () => ({
    root: { padding: '0 2rem' },
    actionButton: { marginRight: '1rem' }
});

export function UserAuthActions (props: UserAuthActionsProps) {
    const {
        classes,
        user,
        firmId
    } = props;

    const [clientFeatures] = useFeatures();

    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const resetUserPasswordValidation = (user: UserRecord) => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: user,
                actionType: ActionTypes.reset_password,
                confirmTitle: `Confirm Resetting Password for ${user.Email}`,
                confirmBody: `Please confirm resetting the password for user ${user.Email}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const resetUserPassword = async (user: UserRecord) => {
        try {
            const response = await Worker.getInstance()
                .resetPassword({
                    FirmId: firmId || '-1',
                    UserId: user.UserId,
                    Email: user.Email
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Reset Password for ${user.Email}`,
                        successBody: `Successfully reset the user's password for ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Reset Password for ${user.Email}`,
                        failureBody: `Could not reset the user's password for ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Reset Password for ${user.Email}`,
                    failureBody: `Something went wrong resetting the user's password for ${user.Email}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const resetUserMfaValidation = (user: UserRecord) => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: user,
                actionType: ActionTypes.reset_mfa,
                confirmTitle: `Confirm Resetting MFA for ${user.Email}`,
                confirmBody: `Please confirm resetting MFA for user ${user.Email}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const resetUserMfa = async (user: UserRecord) => {
        try {
            const response = await Worker.getInstance()
                .resetMFA({
                    FirmId: firmId || '-1',
                    UserId: user.UserId,
                    Email: user.Email
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Reset MFA for ${user.Email}`,
                        successBody: `Successfully reset the user's MFA for ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && response.data.Status === 49 || doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Reset MFA for ${user.Email}`,
                        failureBody: `Could not reset the user's MFA for ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Reset MFA for ${user.Email}`,
                    failureBody: `Something went wrong resetting the user's MFA for ${user.Email}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: user,
                setSelectedItem: () => user,
                preActionValidationAndConfig: {
                    [ActionTypes.reset_password]: resetUserPasswordValidation,
                    [ActionTypes.reset_mfa]: resetUserMfaValidation
                },
                onConfirm: {
                    [ActionTypes.reset_password]: resetUserPassword,
                    [ActionTypes.reset_mfa]: resetUserMfa
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='row'
                spacing={0}
                wrap='nowrap'
            >
                <Grid item>
                    <ActionButton
                        className={classes.actionButton}
                        handleClick={() => resetUserPasswordValidation(user)}
                        id='userAuthActionsResetUserPassword'
                        text='Reset Password'
                        type='muted'
                    />
                </Grid>
                <Grid item>
                    <ActionButton
                        className={classes.actionButton}
                        handleClick={() => resetUserMfaValidation(user)}
                        id='userAuthActionsResetUserMfa'
                        text='Reset MFA'
                        type='muted'
                    />
                </Grid>
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}

export default withStyles(muiStyles)(UserAuthActions);